import { useMemo } from 'react'

import useApprovedGcpProjects from './useApprovedGcpProjects'
import useSingleGcpProject from './useSingleGcpProject'
import {
	getDevelopProjectName,
	inheritsCommonNetwork,
	normalizeCommonNetworkResources
} from '../../helper/gcpProjectSubnetValidation'
import type { CommonNetworkResource, gcpProject } from '../../types/gcpProject'

export type DevelopCommonNetworkLookup = {
	// not_found covers a missing Develop request and one that isn't approved yet.
	status: 'not_applicable' | 'loading' | 'not_found' | 'found'
	developProjectName: string
	resources: CommonNetworkResource[]
}

const normalizeName = (value: unknown): string =>
	typeof value === 'string' ? value.trim().toLowerCase() : ''

// Finds the approved Develop request paired with a Staging/Production request by name.
const useDevelopCommonNetwork = (
	gcpProject: gcpProject,
	enabled: boolean = true
): DevelopCommonNetworkLookup => {
	const details = gcpProject.details || {}
	const applies = enabled && inheritsCommonNetwork(gcpProject.type)
	const developProjectName = applies
		? getDevelopProjectName(details.projectName || details.gcpProjectName)
		: ''

	const { data: approvedProjects, isInitialLoading: isApprovedLoading } =
		useApprovedGcpProjects(Boolean(developProjectName))

	const developProject = useMemo(
		() =>
			developProjectName
				? (approvedProjects || []).find(
						(project) =>
							normalizeName(project.gcpProjectName) ===
							normalizeName(developProjectName)
				  )
				: undefined,
		[approvedProjects, developProjectName]
	)

	const { data: developRequest, isInitialLoading: isDevelopLoading } =
		useSingleGcpProject(
			developProject?.gcpProjectID || '',
			false,
			Boolean(developProject)
		)

	const developResources = developRequest?.details?.commonNetworkResources
	const resources = useMemo(
		() => normalizeCommonNetworkResources(developResources),
		[developResources]
	)

	let status: DevelopCommonNetworkLookup['status'] = 'found'
	if (!applies) {
		status = 'not_applicable'
	} else if (isApprovedLoading || isDevelopLoading) {
		status = 'loading'
	} else if (
		!developProject ||
		!developRequest ||
		developRequest.status === 'ERROR'
	) {
		status = 'not_found'
	}

	return useMemo(
		() => ({ status, developProjectName, resources }),
		[status, developProjectName, resources]
	)
}

export default useDevelopCommonNetwork
